import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { ITableParams } from '../components/data-table/table-params';
import { HttpMethod } from '../enum';
import { PAGE_SIZE } from '../config';
import { tap, catchError } from 'rxjs/operators';

export const QUERY_PARAM_KEYS = {
  PAGE: 'page',
  ORDER_BY: 'orderBy'
} as const;

const DEFAULT_PAGE_SIZE = 20;
const ODATA_COUNT_DEFAULT = 'true';
const DEBUG_LOGGING = false; // Set to true to enable debug logs

// Types
export interface IDataTableState {
  pageIndex: number;
  orderBy: string;
  pageSizeKey?: string;
}

interface IODataParams {
  [key: string]: string | undefined;
  $top: string;
  $count: string;
  $skip?: string;
  $orderBy?: string;
  $filter?: string;
}

@Injectable({ providedIn: 'root' })
export class DataTableService<TItem, TData> {
  public lastResponse: ODataResponse<TItem>;
  
  private _tableStates = new Map<string, IDataTableState>();
  
  private _stateResetSubject = new Subject<string>();
  public stateReset$ = this._stateResetSubject.asObservable();
  
  private _stateChangedSubject = new Subject<{ key: string; state: IDataTableState }>();
  public stateChanged$ = this._stateChangedSubject.asObservable();

  public constructor(private _http: HttpClient) {}

  /**
   * Conditional logging helper - only logs when DEBUG_LOGGING is true
   */
  private _log(message: string, ...args: any[]): void {
    if (DEBUG_LOGGING) {
      console.log(`[DataTableService] ${message}`, ...args);
    }
  }

  private _warn(message: string, ...args: any[]): void {
    if (DEBUG_LOGGING) {
      console.warn(`[DataTableService] ${message}`, ...args);
    }
  }

  private _error(message: string, ...args: any[]): void {
    console.error(`[DataTableService] ${message}`, ...args);
  }

  /**
   * Validates if a number is a valid positive integer
   */
  private _isValidPageSize(value: any): value is number {
    return typeof value === 'number' && value > 0 && !isNaN(value) && Number.isInteger(value);
  }

  public get(dataUrl: string, data: TData, tableParams: ITableParams, method: HttpMethod): Observable<ODataResponse<TItem>> {
    if (!dataUrl) {
      return new Observable<ODataResponse<TItem>>(x => x.complete());
    }

    switch (method) {
      case HttpMethod.Get:
        return this._http.get<ODataResponse<TItem>>('api/' + dataUrl, {
          params: this.getHttpParams(data, tableParams)
        }).pipe(
          tap(response => this.lastResponse = response)
        );

      case HttpMethod.Post:
        return this._http.post<ODataResponse<TItem>>('api/' + dataUrl, data || {}, {
          params: this.getHttpParams(null, tableParams)
        }).pipe(
          tap(response => this.lastResponse = response)
        );
    }
  }

  protected getHttpParams(data: TData, tableParams: ITableParams) {
    let result = new HttpParams();

    if (data) {
      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          result = result.set(key, data[key] ? data[key].toString() : '');
        }
      }
    }

    // Use _getValidPageSize for robust fallback handling
    const pageSize = tableParams.pageSize || this._getValidPageSize();
    const skip = tableParams.pageIndex && pageSize ? (tableParams.pageIndex * pageSize).toString() : null;
    const top = pageSize.toString();
    const count = tableParams.count ? tableParams.count.toString() : 'true';

    result = this.set(result, '$filter', tableParams.filter);
    result = this.set(result, '$orderBy', tableParams.sort);
    result = this.set(result, '$skip', skip);
    result = this.set(result, '$top', top);
    result = this.set(result, '$count', count);

    return result;
  }

  private set(params: HttpParams, key: string, value: string): HttpParams {
    return value ? params.set(key, value) : params;
  }

  /**
   * Saves pagination and sort state for a specific data table
   * @param key - Unique identifier for the table (typically the dataUrl)
   * @param pageIndex - Current page number (0-based)
   * @param orderBy - Current sort order (e.g., "name asc" or "date desc")
   * @param pageSizeKey - Optional page size configuration key
   */
  public saveTableState(key: string, pageIndex: number, orderBy: string, pageSizeKey?: string): void {
    if (!key) return;
    
    const existingState = this._tableStates.get(key);
    const state: IDataTableState = { 
      pageIndex: pageIndex ?? 0,
      orderBy: orderBy ?? '',
      pageSizeKey: pageSizeKey || existingState?.pageSizeKey
    };
    
    this._log('Saving state for', key, ':', { 
      input: { pageIndex, orderBy, pageSizeKey },
      existing: existingState,
      result: state 
    });
    
    this._tableStates.set(key, state);
    this._stateChangedSubject.next({ key, state });
  }

  /**
   * Retrieves saved state for a specific data table
   * @param key - Unique identifier for the table
   * @returns The saved state or null if not found
   */
  public getTableState(key: string): IDataTableState | null {
    if (!key) return null;
    return this._tableStates.get(key) || null;
  }

  /**
   * Checks if a state object has meaningful values (not default/empty)
   */
  private _hasValidState(state: IDataTableState | null): boolean {
    return !!(state && (state.pageIndex > 0 || state.orderBy));
  }

  /**
   * Extracts pagination and sort values from URL query parameters
   */
  private _extractQueryParams(queryParams: any): { pageIndex: number; orderBy: string } {
    const queryPage = parseInt(queryParams[QUERY_PARAM_KEYS.PAGE], 10);
    const queryOrderBy = queryParams[QUERY_PARAM_KEYS.ORDER_BY];
    
    return {
      pageIndex: queryPage > 0 ? queryPage : 0,
      orderBy: queryOrderBy || ''
    };
  }

  /**
   * Clears saved state for a specific data table and notifies subscribers
   * @param key - Unique identifier for the table
   */
  public clearTableState(key: string): void {
    if (!key) return;
    
    this._tableStates.delete(key);
    this._stateResetSubject.next(key);
  }

  /**
   * Resolves table state from memory (Map) or URL query params, in that priority order
   * Used by route resolvers to restore table state on page load/refresh
   * @param dataUrl - Unique identifier for the table
   * @param queryParams - URL query parameters
   * @param pageSizeKey - Optional page size configuration key
   * @returns Resolved state with pageIndex, orderBy, and pageSizeKey
   */
  public getTableStateForResolver(dataUrl: string, queryParams: any, pageSizeKey?: string): IDataTableState {
    const savedState = this.getTableState(dataUrl);
    const resolvedPageSizeKey = savedState?.pageSizeKey || pageSizeKey;
    
    this._log('getTableStateForResolver:', { dataUrl, savedState, queryParams, pageSizeKey });
    
    // Priority 1: Use saved state from Map (navigation between views)
    if (this._hasValidState(savedState)) {
      this._log('Using saved state from Map');
      return { 
        pageIndex: savedState.pageIndex, 
        orderBy: savedState.orderBy, 
        pageSizeKey: resolvedPageSizeKey 
      };
    }
    
    // Priority 2: Use query params (page refresh, deep links, bookmarks)
    const fromQuery = this._extractQueryParams(queryParams);
    if (this._hasValidState(fromQuery)) {
      this._log('Using query params, syncing to Map:', fromQuery);
      this.saveTableState(dataUrl, fromQuery.pageIndex, fromQuery.orderBy, resolvedPageSizeKey);
      return { ...fromQuery, pageSizeKey: resolvedPageSizeKey };
    }
    
    // Priority 3: Use defaults
    this._log('No valid state, using defaults');
    return { pageIndex: 0, orderBy: '', pageSizeKey: resolvedPageSizeKey };
  }

  public getODataParamsForResolver(dataUrl: string, queryParams: any, pageSizeKey?: string): IODataParams {
    const state = this.getTableStateForResolver(dataUrl, queryParams, pageSizeKey);
    const pageSize = this._getValidPageSize(state.pageSizeKey);
    const skip = state.pageIndex * pageSize;
    
    this._log('getODataParams:', { 
      dataUrl, 
      pageSizeKey: state.pageSizeKey, 
      pageSize, 
      pageIndex: state.pageIndex,
      skip,
      orderBy: state.orderBy
    });
    
    const params: IODataParams = {
      $top: `${pageSize}`,
      $count: ODATA_COUNT_DEFAULT
    };
    
    if (skip > 0) params.$skip = `${skip}`;
    if (state.orderBy) params.$orderBy = state.orderBy;
    
    return params;
  }

  private _getValidPageSize(pageSizeKey?: string): number {
    // Level 1: Try provided key (with or without PAGE_SIZE_ prefix)
    if (pageSizeKey) {
      const directLookup = PAGE_SIZE[pageSizeKey];
      if (this._isValidPageSize(directLookup)) {
        this._log('PageSize from key:', pageSizeKey, '=', directLookup);
        return directLookup;
      }
      
      // Try with PAGE_SIZE_ prefix if not already present
      if (!pageSizeKey.startsWith('PAGE_SIZE_')) {
        const prefixedLookup = PAGE_SIZE['PAGE_SIZE_' + pageSizeKey];
        if (this._isValidPageSize(prefixedLookup)) {
          this._log('PageSize from prefixed key:', 'PAGE_SIZE_' + pageSizeKey, '=', prefixedLookup);
          return prefixedLookup;
        }
      }
      
      this._warn('PageSize key lookup failed for:', pageSizeKey);
    }
    
    // Level 2: Fallback to DEFAULT
    const defaultPageSize = PAGE_SIZE['DEFAULT'];
    if (this._isValidPageSize(defaultPageSize)) {
      this._log('PageSize from DEFAULT =', defaultPageSize);
      return defaultPageSize;
    }
    
    // Level 3: Ultimate hardcoded fallback
    this._error('CRITICAL: PAGE_SIZE.DEFAULT is invalid - using hardcoded fallback:', DEFAULT_PAGE_SIZE);
    return DEFAULT_PAGE_SIZE;
  }

  public hasOrderByChanged(key: string, currentOrderBy: string): boolean {
    if (!key) return false;
    
    const state = this._tableStates.get(key);
    if (!state || !state.orderBy) {
      this._log('hasOrderByChanged: No existing state or orderBy');
      return false;
    }
    
    // Extract just the column name from orderBy (e.g., "name asc" -> "name")
    const extractColumn = (orderBy: string): string => {
      if (!orderBy) return '';
      const parts = orderBy.split(',');
      const userSort = parts[parts.length - 1].trim();
      return userSort.split(' ')[0];
    };
    
    const oldColumn = extractColumn(state.orderBy);
    const newColumn = extractColumn(currentOrderBy);
    const changed = oldColumn !== newColumn && newColumn !== '';
    
    this._log('hasOrderByChanged:', {
      key,
      oldOrderBy: state.orderBy,
      newOrderBy: currentOrderBy,
      oldColumn,
      newColumn,
      changed
    });
    
    return changed;
  }

  public handleResolverRequest<T>(
    dataUrl: string, 
    requestFn: (params: IODataParams) => Observable<T>,
    queryParams: any,
    pageSizeKey?: string
  ): Observable<T> {
    const hadState = this._hasValidState(this.getTableState(dataUrl));
    const params = this.getODataParamsForResolver(dataUrl, queryParams, pageSizeKey);
    
    // Final safety check: ensure $top is valid
    this._validateTopParam(params);
    
    return requestFn(params).pipe(
      catchError(error => {
        // If we had saved state and request failed, try again with clean defaults
        if (hadState) {
          this._warn('Resolver request failed with saved state, clearing and retrying with defaults:', error);
          this.clearTableState(dataUrl);
          
          const defaultParams: IODataParams = {
            $top: `${this._getValidPageSize(pageSizeKey)}`,
            $count: ODATA_COUNT_DEFAULT
          };
          
          this._log('Retry with params:', defaultParams);
          return requestFn(defaultParams);
        }
        
        throw error;
      })
    );
  }

  /**
   * Validates the $top parameter and fixes it if invalid
   * This is a final safety net to catch any edge cases
   */
  private _validateTopParam(params: IODataParams): void {
    const topValue = parseInt(params.$top, 10);
    const isInvalid = !params.$top || 
                      params.$top === 'undefined' || 
                      params.$top === 'null' || 
                      isNaN(topValue) || 
                      topValue <= 0;
    
    if (isInvalid) {
      this._error('CRITICAL: $top is invalid!', params.$top, '- forcing to', DEFAULT_PAGE_SIZE);
      params.$top = `${DEFAULT_PAGE_SIZE}`;
    }
  }

}

export class ODataResponse<TItem> {
  public items: TItem[];
  public count: number;
  public searchKey: string;
  public page: number;
  public pageSize: number;
  public sort: string;
  public statusCount?: { [key: number]: number; };
}
